import { useCallback, useEffect, useState } from 'react';

import { isSSR } from '@telialt/utils/src/helpers';

type SetStateParams<T> = {
    modifySearchParams?: (params: URLSearchParams) => void;
    modifyState?: (state: T) => T;
};

const getQueryParamValue = <T>(paramName: string) => {
    if (isSSR()) {
        return undefined;
    }
    const queryString = new URLSearchParams(window.location.search);

    return queryString.get(paramName) as T;
};

export const useBrowserQueryState = <State extends string>(
    paramName: string,
    defaultValue?: State,
    initialValue?: State,
) => {
    const [internalState, setInternalState] = useState<State | undefined>(
        getQueryParamValue<State>(paramName) || initialValue,
    );

    useEffect(() => {
        setInternalState(getQueryParamValue<State>(paramName));
        const updateState = () => setInternalState(getQueryParamValue<State>(paramName));

        window.addEventListener('popstate', updateState);

        return () => window.removeEventListener('popstate', updateState);
    }, [paramName]);

    const setState = useCallback(
        <T>(value: State, { modifySearchParams, modifyState }: SetStateParams<T> = {}) => {
            const url = new URL(window.location.href);

            if (value !== defaultValue) {
                url.searchParams.set(paramName, value);
            } else {
                url.searchParams.delete(paramName);
            }

            modifySearchParams?.(url.searchParams);
            const updatedState = modifyState ? modifyState(window.history.state) : window.history.state;

            window.history.pushState(updatedState, '', url.toString());
            window.dispatchEvent(new CustomEvent<State>('popstate'));
        },
        [defaultValue, paramName],
    );

    return [internalState ?? defaultValue, setState] as const;
};
